import axios from 'axios'
import TomSelect from 'tom-select'
import Lang from './Lang.js'

export default class FontPicker {
    // <div> element in which the font picker is rendered
    #element

    // <select> element holding all the font names
    #fontSelector

    // Url where the whole list of fonts can be retrieved from
    #fontListUrl = 'https://fonts.bunny.net/list'

    // The dataset values
    #props

    constructor(props) {
        this.#props = props

        this.checkRequiredProps()

        this.#element = document.getElementById(props.id)

        this.generateUI()
    }

    static initAll(selector) {
        Array.from(document.querySelectorAll(selector), function (element) {
            new FontPicker(element.dataset)
        })
    }

    checkRequiredProps() {
        if (!this.#props.id) {
            throw Error(`Missing [data-id="should_equal_element_id"] for font picker.`)
        }

        if (!this.#props.name) {
            throw Error(`Missing [data-name="choose_your_own_name"] for font picker.`)
        }
    }

    generateUI() {
        this.#fontSelector = document.createElement('select')
        this.#fontSelector.classList.add('form-control')
        this.#fontSelector.setAttribute('name', this.#props.name)
        this.#element.appendChild(this.#fontSelector)

        const statusText = document.createElement('option')
        statusText.text = 'Loading...'
        this.#fontSelector.add(statusText)

        this.getFonts()
            .then((fonts) => {
                this.#fontSelector.removeChild(this.#fontSelector.firstChild)
                const defaultOption = document.createElement('option')
                defaultOption.text = this.#props.placeholder ?? 'Select font...'
                defaultOption.value = ''
                defaultOption.selected = true
                this.#fontSelector.add(defaultOption)

                this.generateFontList(fonts)

                new TomSelect(this.#fontSelector, {
                    allowEmptyOption: false,
                    create: false,
                    selectOnTab: true,
                    maxItems: 1,
                    maxOptions: null,
                    hidePlaceholder: true,
                    placeholder: this.#props.placeholder ?? 'Select font...',
                    plugins: {
                        clear_button: {
                            title: Lang.get('general.remove'),
                        },
                    },
                    render: {
                        no_results: function () {
                            return (
                                '<div class="no-results">' +
                                Lang.get('general.no_results') +
                                '</div>'
                            )
                        },
                    },
                })
            })
            .catch((err) => {
                this.#fontSelector.removeChild(this.#fontSelector.firstChild)
                const defaultOption = document.createElement('option')
                defaultOption.text = 'Error loading fonts...'
                this.#fontSelector.add(defaultOption)

                console.error('Error trying to fetch the list of available fonts')
                console.error(err)
            })
    }

    generateFontList(fonts) {
        fonts.forEach((font) => {
            this.addFontOption(font)
        })
    }

    addFontOption(font) {
        const option = document.createElement('option')
        option.text = this.formatFontName(font)
        option.value = font
        if (this.#props.selected) {
            option.selected = this.#props.selected === font
        }

        this.#fontSelector.appendChild(option)
    }

    formatFontName(name) {
        return name
            .replace('-', ' ')
            .toLowerCase()
            .split(' ')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')
    }

    async getFonts() {
        if (localStorage.hasOwnProperty('bunny-fonts')) {
            return JSON.parse(localStorage.getItem('bunny-fonts'))
        }

        const url = new URL(this.#fontListUrl)
        const response = await axios.get(url.href)

        if (response.status !== 200) {
            throw Error('The font list cannot be loaded.')
        }

        const list = Object.keys(response.data)

        localStorage.setItem('bunny-fonts', JSON.stringify(list))

        return list
    }
}
